import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { ID } from '@/types/model'
import { useAppSelector } from '.'
import {
  getDeviceIneligiblityMessages,
  selectProgram,
} from '@/reducers/programsNew'
import { match } from 'ts-pattern'
import { DeviceEligibilityOverview } from '@/types/programs-new/programs-new'
import { isDeviceAllowedInProgram } from '@/app/hooks/useUtilityPrograms'
import { UUID } from '@/types/uuid'
import { isUUIDv4 } from '@/utils/uuid/uuid.utils'

export default function useEligibleForProgram({
  programId,
  deviceId,
  deviceType,
}: {
  programId: ID
  deviceId?: ID | UUID | string
  deviceType?: DeviceType
}) {
  const program = useAppSelector(selectProgram(programId))

  return isDeviceEligibleForProgram(
    program?.device_eligibility_overview,
    deviceId,
    deviceType,
  )
}

export function isDeviceEligibleForProgram(
  programEligibilityOverview: DeviceEligibilityOverview | undefined,
  deviceId?: ID | UUID | string,
  deviceType?: DeviceType,
) {
  const profileEligibility =
    programEligibilityOverview?.profile?.is_eligible ?? false

  if (deviceId) {
    deviceId = isUUIDv4(deviceId)
      ? deviceId
      : Number.parseInt(deviceId.toString())
  }

  let deviceEligibility = true
  if (deviceId && deviceType && programEligibilityOverview) {
    const ineligibilityMessages = getDeviceIneligiblityMessages(
      programEligibilityOverview.devices,
    )
    const deviceAllowed = isDeviceAllowedInProgram(
      deviceId,
      deviceType,
      programEligibilityOverview,
      ineligibilityMessages,
    )

    const deviceProgramEligible = match(deviceType)
      .with(
        'vehicle',
        () =>
          programEligibilityOverview.devices?.vehicles?.find(
            (v) => v.id === deviceId,
          )?.is_eligible ?? true,
      )
      .with(
        'thermostat',
        () =>
          programEligibilityOverview.devices?.thermostats?.find(
            (t) => t.id === deviceId,
          )?.is_eligible ?? true,
      )
      .with(
        'vehicleCharger',
        () =>
          programEligibilityOverview.devices?.chargers?.find(
            (c) => c.id === deviceId,
          )?.is_eligible ?? true,
      )
      .with(
        'energySystem',
        () =>
          programEligibilityOverview.devices?.energy_systems?.find(
            (e) => e.id === deviceId,
          )?.is_eligible ?? true,
      )
      .with(
        'home',
        () =>
          programEligibilityOverview.devices?.behavioral?.find(
            (e) => e.id === deviceId,
          )?.is_eligible ?? true,
      )
      .exhaustive()

    deviceEligibility = (deviceAllowed && deviceProgramEligible) ?? false
  }

  return {
    profileEligibility,
    deviceEligibility,
    isEligible: profileEligibility && deviceEligibility,
  }
}
